"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  SongwriterBlock,
  SongwriterBlockType,
} from "../types/songwriter.types";
import * as SongwriterService from "../service/songwriter.service";

// Start with the locked title/type header blocks and a first beat block by default
const initialRawBlocks: SongwriterBlock[] = [
  { id: "initname", type: "SONG_NAME", text: "" },
  { id: "inittype", type: "SONG_TYPE", text: "" },
];

export function setCaretAtEnd(element: HTMLElement) {
  element.focus();
  try {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  } catch (err) {
    // Silently capture any initial selection layout edge-cases
  }
}

export function getCaretCharacterOffsetWithin(element: HTMLElement) {
  let caretOffset = 0;
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    caretOffset = preCaretRange.toString().length;
  }
  return caretOffset;
}

export function setCaretPosition(element: HTMLElement, offset: number) {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  let currentOffset = 0;
  let found = false;

  function traverseNodes(node: Node) {
    if (found) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.nodeValue?.length || 0;
      if (currentOffset + length >= offset) {
        range.setStart(node, offset - currentOffset);
        range.collapse(true);
        found = true;
      } else {
        currentOffset += length;
      }
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        traverseNodes(node.childNodes[i]);
        if (found) break;
      }
    }
  }

  traverseNodes(element);

  if (!found) {
    try {
      range.setStart(element, 0);
      range.collapse(true);
    } catch (err) {}
  }

  sel.removeAllRanges();
  sel.addRange(range);
}

export interface UseSongwriterOptions {
  blocks?: SongwriterBlock[];
  onSave?: (blocks: SongwriterBlock[]) => void | Promise<void>;
  isLocked?: boolean;
}

export function useSongwriter(options?: UseSongwriterOptions) {
  const [blocks, setBlocks] = useState<SongwriterBlock[]>(() => {
    const raw =
      options?.blocks && options.blocks.length > 0
        ? options.blocks
        : initialRawBlocks;
    return SongwriterService.assignInitialPositions(raw);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [focusedBlockId, setFocusedBlockId] = useState<string>("");
  const [newBlockId, setNewBlockId] = useState<string | null>(null);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const isLocked = options?.isLocked ?? false;

  const hasInitializedRef = useRef(
    !!(options?.blocks && options.blocks.length > 0),
  );

  const skipAutosaveRef = useRef(false);

  const onSaveRef = useRef(options?.onSave);
  useEffect(() => {
    onSaveRef.current = options?.onSave;
  }, [options?.onSave]);

  const latestBlocksRef = useRef(blocks);
  useEffect(() => {
    latestBlocksRef.current = blocks;
  }, [blocks]);

  // Client-side mount parser to prevent hydration flashes
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (options?.blocks && options.blocks.length > 0 && !hasInitializedRef.current) {
        skipAutosaveRef.current = true;
        setBlocks(SongwriterService.assignInitialPositions(options.blocks));
        if (options.blocks[0]?.id) {
          setFocusedBlockId(options.blocks[0].id);
        }
        hasInitializedRef.current = true;
      } else if (!hasInitializedRef.current) {
        // Default focus to first block if no draft exists
        if (blocks[0]?.id) {
          setFocusedBlockId(blocks[0].id);
        }
      }
      setIsLoading(false);
    }
  }, [options?.blocks]);

  // Click outside / Notion-style Canvas Whitespace Appending (8th point of docs/collaboration_research.md)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInsideBlock = target.closest("[data-block-id]");
      const isInsideToolbar = target.closest(".sticky");
      const isInsideSuggestions = target.closest('[role="listbox"]');
      const isInsideEditorSheet = target.closest(
        '[data-screenplay-editor="true"]',
      );

      if (!isInsideBlock && !isInsideToolbar && !isInsideSuggestions) {
        if (!isInsideEditorSheet) {
          setFocusedBlockId((prev) => (prev !== "" ? "" : prev));
        } else {
          if (latestBlocksRef.current.length > 0) {
            const sorted = [...latestBlocksRef.current].sort((a, b) =>
              SongwriterService.comparePositions(a.position, b.position),
            );
            const lastBlock = sorted[sorted.length - 1];
            const lastBlockEl = refs.current[lastBlock.id];
            if (lastBlockEl) {
              const rect = lastBlockEl.getBoundingClientRect();
              if (e.clientY > rect.bottom && !isLocked) {
                // User clicked below the last block! Let's append an empty block at the end
                const { newBlocks, newBlockId } =
                  SongwriterService.addBlockAfter(
                    latestBlocksRef.current,
                    lastBlock.id,
                  );
                setBlocks(newBlocks);
                setNewBlockId(newBlockId);
              }
            }
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLocked]);

  // Keeps track of the last synchronized text to prevent cursor-jumping and reverse typing
  const lastSyncedTextRef = useRef<Record<string, string>>({});

  // Autosave status state matching screenplay editor state types
  const [autosaveStatus, setAutosaveStatus] = useState<
    "idle" | "typing" | "saving" | "saved" | "error"
  >("idle");

  const isFirstAutosaveRender = useRef(true);
  const prevBlocksRef = useRef<SongwriterBlock[]>(blocks);

  // Synchronize prevBlocksRef when blocks load on mount
  useEffect(() => {
    if (!isLoading) {
      prevBlocksRef.current = blocks;
    }
  }, [isLoading]);

  // Debounced Autosave State Machine with typing, saving, and saved statuses
  useEffect(() => {
    if (isFirstAutosaveRender.current) {
      isFirstAutosaveRender.current = false;
      prevBlocksRef.current = blocks;
      return;
    }

    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      prevBlocksRef.current = blocks;
      return;
    }

    const hasChanged =
      blocks.length !== prevBlocksRef.current.length ||
      blocks.some(
        (b, i) =>
          b.text !== prevBlocksRef.current[i]?.text ||
          b.type !== prevBlocksRef.current[i]?.type,
      );

    if (!hasChanged) {
      return;
    }

    setAutosaveStatus("typing");
    prevBlocksRef.current = blocks;

    const timer = setTimeout(() => {
      if (onSaveRef.current) {
        setAutosaveStatus("saving");

        const performSave = async () => {
          try {
            if (onSaveRef.current) {
              await onSaveRef.current(blocks);
            }
            // Delay to let the spinner render nicely
            await new Promise((resolve) => setTimeout(resolve, 800));
            setAutosaveStatus("saved");
            setTimeout(() => {
              setAutosaveStatus((prev) => (prev === "saved" ? "idle" : prev));
            }, 2000);
          } catch (error) {
            console.error("Autosave failed:", error);
            setAutosaveStatus("error");
          }
        };

        void performSave();
      } else {
        setAutosaveStatus("idle");
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [blocks]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (autosaveStatus === "typing" || autosaveStatus === "saving") {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [autosaveStatus]);

  const autosaveStatusRef = useRef(autosaveStatus);
  useEffect(() => {
    autosaveStatusRef.current = autosaveStatus;
  }, [autosaveStatus]);

  useEffect(() => {
    return () => {
      // Flush any pending database saving on unmount
      if (autosaveStatusRef.current === "typing" && onSaveRef.current) {
        onSaveRef.current(latestBlocksRef.current);
      }
    };
  }, []);

  // Synchronize outer state changes (e.g., initial load or import) to DOM nodes manually without clashing with active cursor typing
  useEffect(() => {
    blocks.forEach((block) => {
      const element = refs.current[block.id];
      if (element) {
        if (
          lastSyncedTextRef.current[block.id] !== block.text &&
          document.activeElement !== element
        ) {
          element.textContent = block.text;
          lastSyncedTextRef.current[block.id] = block.text;
        }
      }
    });
  }, [blocks]);

  // Handle focus sync on newly created blocks and place cursor at end
  useEffect(() => {
    if (newBlockId && refs.current[newBlockId]) {
      const el = refs.current[newBlockId];
      const block = blocks.find((b) => b.id === newBlockId);
      if (el && block) {
        el.textContent = block.text;
        lastSyncedTextRef.current[newBlockId] = block.text;
        setCaretAtEnd(el);
      }
      setFocusedBlockId(newBlockId);
      setNewBlockId(null);
    }
  }, [newBlockId, blocks]);

  const handleBlockTextChange = useCallback(
    (id: string, text: string) => {
      const block = blocks.find((b) => b.id === id);
      if (!block) return;

      let processedText = text;
      if (block.type === "SONG_BEAT") {
        // Extract text between "[BEAT: " and "]"
        const clean = text.replace(/[\[\]]/g, "").replace(/^BEAT:\s*/i, "");
        processedText = `[BEAT: ${clean.toUpperCase()}]`;
      }

      setBlocks((prev) =>
        SongwriterService.updateBlock(prev, id, processedText),
      );

      if (block.type === "SONG_BEAT") {
        const el = refs.current[id];
        if (el) {
          const offset = getCaretCharacterOffsetWithin(el);
          const beforeCaretText = text.substring(0, offset);
          const cleanBeforeCaret = beforeCaretText
            .replace(/[\[\]]/g, "")
            .replace(/^BEAT:\s*/i, "");
          const newOffset = 7 + cleanBeforeCaret.length;

          if (el.textContent !== processedText) {
            el.textContent = processedText;
            lastSyncedTextRef.current[id] = processedText;
            setCaretPosition(el, newOffset);
          } else {
            lastSyncedTextRef.current[id] = text;
          }
        }
      } else {
        lastSyncedTextRef.current[id] = text;
      }
    },
    [blocks],
  );

  const handleBlockTypeChange = useCallback(
    (newType: SongwriterBlockType) => {
      if (!focusedBlockId) return;
      setBlocks((prev) => {
        const updated = SongwriterService.changeBlockType(
          prev,
          focusedBlockId,
          newType,
        );
        const updatedBlock = updated.find((b) => b.id === focusedBlockId);
        // Wait for DOM layout to settle and place cursor at the end
        setTimeout(() => {
          const el = refs.current[focusedBlockId];
          if (el && updatedBlock) {
            el.textContent = updatedBlock.text;
            lastSyncedTextRef.current[focusedBlockId] = updatedBlock.text;
            setCaretAtEnd(el);
          }
        }, 10);
        return updated;
      });
    },
    [focusedBlockId],
  );

  const handleFocus = useCallback((id: string) => {
    setFocusedBlockId(id);
    setTimeout(() => {
      const el = refs.current[id];
      if (el) {
        setCaretAtEnd(el);
      }
    }, 10);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      const block = blocks.find((b) => b.id === id);
      if (block?.type === "SONG_BEAT") {
        const el = refs.current[id];
        if (el) {
          const currentOffset = getCaretCharacterOffsetWithin(el);
          const textLength = el.textContent?.length || 0;
          const minOffset = 7;
          const maxOffset = Math.max(7, textLength - 1);

          if (currentOffset < minOffset) {
            setCaretPosition(el, minOffset);
          } else if (currentOffset > maxOffset) {
            setCaretPosition(el, maxOffset);
          }
        }
      }
    },
    [blocks],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, id: string, text: string) => {
      if (isLocked) return;
      const sorted = [...blocks].sort((a, b) =>
        SongwriterService.comparePositions(a.position, b.position),
      );
      const currentIndex = sorted.findIndex((b) => b.id === id);

      // Enter Key -> Transition or Append Next Block
      if (e.key === "Enter") {
        e.preventDefault();
        const currentBlock = blocks.find((b) => b.id === id);
        if (
          currentBlock?.type === "SONG_NAME" ||
          currentBlock?.type === "SONG_TYPE"
        ) {
          // Essential headers cannot be split; simply transition focus downwards
          if (currentIndex < sorted.length - 1) {
            const nextBlock = sorted[currentIndex + 1];
            const el =
              refs.current[nextBlock.id] ||
              (document.querySelector(
                `[data-block-id="${nextBlock.id}"] [contenteditable="true"]`,
              ) as HTMLDivElement);
            if (el) {
              setCaretAtEnd(el);
            }
          } else {
            // If SONG_TYPE is the final block, append a new block below
            const { newBlocks, newBlockId: createdId } =
              SongwriterService.addBlockAfter(blocks, id);
            setBlocks(newBlocks);
            setNewBlockId(createdId);
          }
          return;
        }

        const { newBlocks, newBlockId: createdId } =
          SongwriterService.addBlockAfter(blocks, id);
        setBlocks(newBlocks);
        setNewBlockId(createdId);
        return;
      }

      // Backspace Key on Empty Content -> Transition Focus or Delete Block
      const currentBlock = blocks.find((b) => b.id === id);
      const isTextEmpty = text.trim() === "";
      const isBeatEmpty =
        currentBlock?.type === "SONG_BEAT" &&
        text
          .replace(/[\[\]]/g, "")
          .replace(/^BEAT:\s*/i, "")
          .trim() === "";

      if (e.key === "Backspace" && (isTextEmpty || isBeatEmpty)) {
        e.preventDefault();
        if (
          currentBlock?.type === "SONG_NAME" ||
          currentBlock?.type === "SONG_TYPE"
        ) {
          // Essential headers cannot be deleted; simply transition focus upwards
          if (currentIndex > 0) {
            const prevBlock = sorted[currentIndex - 1];
            const el =
              refs.current[prevBlock.id] ||
              (document.querySelector(
                `[data-block-id="${prevBlock.id}"] [contenteditable="true"]`,
              ) as HTMLDivElement);
            if (el) {
              setCaretAtEnd(el);
            }
          }
          return;
        }

        const { newBlocks, nextFocusedId } = SongwriterService.deleteBlock(
          blocks,
          id,
        );
        setBlocks(newBlocks);
        if (nextFocusedId) {
          setFocusedBlockId(nextFocusedId);
          setTimeout(() => {
            const el =
              refs.current[nextFocusedId] ||
              (document.querySelector(
                `[data-block-id="${nextFocusedId}"] [contenteditable="true"]`,
              ) as HTMLDivElement);
            if (el) {
              setCaretAtEnd(el);
            }
          }, 10);
        }
        return;
      }

      // Tab Key -> Quick Toggle between lyrics and music cues
      if (e.key === "Tab") {
        e.preventDefault();
        if (currentBlock) {
          if (currentBlock.type === "SONG_LYRICS") {
            handleBlockTypeChange("SONG_BEAT");
          } else if (currentBlock.type === "SONG_BEAT") {
            handleBlockTypeChange("SONG_LYRICS");
          }
        }
        return;
      }

      // Ctrl + Up/Down Arrow -> Quick toggle block formats (Lyrics vs Beat Cue)
      if (e.ctrlKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
        if (
          currentBlock &&
          (currentBlock.type === "SONG_LYRICS" ||
            currentBlock.type === "SONG_BEAT")
        ) {
          const nextType =
            currentBlock.type === "SONG_LYRICS" ? "SONG_BEAT" : "SONG_LYRICS";
          handleBlockTypeChange(nextType);
        }
        return;
      }

      // Up Arrow Navigation
      if (e.key === "ArrowUp" && currentIndex > 0) {
        e.preventDefault();
        const prevBlock = sorted[currentIndex - 1];
        const el = refs.current[prevBlock.id];
        if (el) {
          setCaretAtEnd(el);
        }
        return;
      }

      // Down Arrow Navigation
      if (e.key === "ArrowDown" && currentIndex < sorted.length - 1) {
        e.preventDefault();
        const nextBlock = sorted[currentIndex + 1];
        const el = refs.current[nextBlock.id];
        if (el) {
          setCaretAtEnd(el);
        }
        return;
      }
    },
    [blocks, handleBlockTypeChange, isLocked],
  );

  // Export Lyrics to Text Draft
  const exportToTxt = useCallback(() => {
    const sorted = [...blocks].sort((a, b) =>
      SongwriterService.comparePositions(a.position, b.position),
    );
    let txt = "";
    sorted.forEach((block) => {
      if (block.type === "SONG_NAME") {
        txt += `\n=== ${block.text.toUpperCase() || "UNTITLED SONG"} ===\n`;
      } else if (block.type === "SONG_TYPE") {
        txt += `Genre/Type: ${block.text}\n\n`;
      } else if (block.type === "SONG_BEAT") {
        txt += `\n${block.text.toUpperCase()}\n`;
      } else if (block.type === "SONG_LYRICS") {
        txt += `  ${block.text}\n`;
      }
    });

    const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lyric_sheet_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [blocks]);

  return {
    blocks,
    isLoading,
    isLocked,
    refs,
    focusedBlockId,
    newBlockId,
    autosaveStatus,
    setFocusedBlockId,
    setNewBlockId,
    handleBlockTextChange,
    handleBlockTypeChange,
    handleKeyDown,
    handleFocus,
    handleSelect,
    exportToTxt,
  };
}
